import { Vec2 } from '../utils/math';
import { Vector2, Bounds, BossType, DotEffect } from '../types';
import { Renderer } from '../renderer/Renderer';
import {
  BOSS_RADIUS,
  BOSS_BASE_SPEED,
  BOSS_COLORS,
  BOSS_HP,
  BOSS_INVULNERABILITY_MS,
} from '../utils/constants';
import { circleCollision } from '../utils/math';

/**
 * Boss entity - a large roaming enemy that applies buffs to all dots while alive.
 *
 * Each boss type has a unique behavior and applies a unique DotEffect.
 * Bosses have multiple HP and become briefly invulnerable after being hit.
 */
export class Boss {
  position: Vec2;
  velocity: Vector2;
  readonly type: BossType;
  readonly radius: number = BOSS_RADIUS;
  readonly maxHp: number;
  hp: number;
  private invulnerableUntil: number = 0;
  private isDeadFlag: boolean = false;
  private elapsedMs: number = 0;
  private wanderAngle: number;

  constructor(type: BossType, x: number, y: number) {
    this.position = new Vec2(x, y);
    this.type = type;
    this.maxHp = BOSS_HP[type] ?? 3;
    this.hp = this.maxHp;
    this.velocity = { x: 0, y: 0 };
    this.wanderAngle = Math.random() * Math.PI * 2;

    // Per-type speed tuning
    switch (type) {
      case BossType.SPEED_DEMON:
        this.velocity = this.angleToVec(this.wanderAngle, BOSS_BASE_SPEED * 1.6);
        break;
      case BossType.MAGNET_LORD:
        this.velocity = this.angleToVec(this.wanderAngle, BOSS_BASE_SPEED * 0.7);
        break;
      case BossType.SHIELD_MOTHER:
        this.velocity = this.angleToVec(this.wanderAngle, BOSS_BASE_SPEED * 0.5);
        break;
      case BossType.SPLITTER_KING:
        this.velocity = this.angleToVec(this.wanderAngle, BOSS_BASE_SPEED * 0.9);
        break;
    }
  }

  private angleToVec(angle: number, speed: number): Vector2 {
    return { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
  }

  /**
   * The DotEffect this boss applies while alive
   */
  getEffect(): DotEffect {
    switch (this.type) {
      case BossType.SPEED_DEMON: return DotEffect.SPEED_BOOST;
      case BossType.SHIELD_MOTHER: return DotEffect.SHIELD;
      case BossType.MAGNET_LORD: return DotEffect.MAGNET;
      case BossType.SPLITTER_KING: return DotEffect.SPLITTING;
    }
  }

  getColor(): string {
    return BOSS_COLORS[this.type] ?? '#FFFFFF';
  }

  update(dt: number, bounds: Bounds, playerPosition: Vector2): void {
    if (this.isDeadFlag) return;
    this.elapsedMs += dt * 1000;

    // Behavior per type
    switch (this.type) {
      case BossType.SPEED_DEMON:
        // Erratic fast wandering - change direction occasionally
        if (Math.random() < 0.02) {
          this.wanderAngle += (Math.random() - 0.5) * 1.2;
          const speed = BOSS_BASE_SPEED * 1.6;
          this.velocity = this.angleToVec(this.wanderAngle, speed);
        }
        break;

      case BossType.MAGNET_LORD:
        // Slowly drift toward the player but keep some distance
        {
          const dx = playerPosition.x - this.position.x;
          const dy = playerPosition.y - this.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const desired = 250;
          if (dist > 0) {
            const factor = (dist - desired) / dist;
            const targetVx = (dx / dist) * BOSS_BASE_SPEED * 0.7 * Math.sign(factor);
            const targetVy = (dy / dist) * BOSS_BASE_SPEED * 0.7 * Math.sign(factor);
            this.velocity.x += (targetVx - this.velocity.x) * dt * 1.5;
            this.velocity.y += (targetVy - this.velocity.y) * dt * 1.5;
          }
        }
        break;

      case BossType.SHIELD_MOTHER:
        // Slow meandering
        if (Math.random() < 0.01) {
          this.wanderAngle += (Math.random() - 0.5) * 0.6;
          const speed = BOSS_BASE_SPEED * 0.5;
          this.velocity = this.angleToVec(this.wanderAngle, speed);
        }
        break;

      case BossType.SPLITTER_KING:
        // Steady movement with mild wander
        if (Math.random() < 0.015) {
          this.wanderAngle += (Math.random() - 0.5) * 0.8;
          const speed = BOSS_BASE_SPEED * 0.9;
          this.velocity = this.angleToVec(this.wanderAngle, speed);
        }
        break;
    }

    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;

    // Bounce off walls
    const r = this.radius;
    if (this.position.x < r) {
      this.position.x = r;
      this.velocity.x = Math.abs(this.velocity.x);
      this.wanderAngle = Math.atan2(this.velocity.y, this.velocity.x);
    } else if (this.position.x > bounds.width - r) {
      this.position.x = bounds.width - r;
      this.velocity.x = -Math.abs(this.velocity.x);
      this.wanderAngle = Math.atan2(this.velocity.y, this.velocity.x);
    }
    if (this.position.y < r) {
      this.position.y = r;
      this.velocity.y = Math.abs(this.velocity.y);
      this.wanderAngle = Math.atan2(this.velocity.y, this.velocity.x);
    } else if (this.position.y > bounds.height - r) {
      this.position.y = bounds.height - r;
      this.velocity.y = -Math.abs(this.velocity.y);
      this.wanderAngle = Math.atan2(this.velocity.y, this.velocity.x);
    }
  }

  /**
   * Returns true if boss is currently vulnerable to a weapon hit
   */
  isVulnerable(): boolean {
    return !this.isDeadFlag && Date.now() >= this.invulnerableUntil;
  }

  /**
   * Attempt to damage the boss from a weapon effect at the given position/radius.
   * Returns true if damage was actually applied.
   */
  tryDamage(weaponPos: Vector2, weaponRadius: number): boolean {
    if (this.isDeadFlag || !this.isVulnerable()) return false;
    if (!circleCollision(this.position, this.radius, weaponPos, weaponRadius)) return false;
    this.hp -= 1;
    if (this.hp <= 0) {
      this.isDeadFlag = true;
    } else {
      this.invulnerableUntil = Date.now() + BOSS_INVULNERABILITY_MS;
    }
    return true;
  }

  /**
   * Direct hit-damage (used by contact weapons like chainsaw)
   */
  takeDirectHit(): boolean {
    if (this.isDeadFlag || !this.isVulnerable()) return false;
    this.hp -= 1;
    if (this.hp <= 0) {
      this.isDeadFlag = true;
    } else {
      this.invulnerableUntil = Date.now() + BOSS_INVULNERABILITY_MS;
    }
    return true;
  }

  isDead(): boolean {
    return this.isDeadFlag;
  }

  /**
   * Returns true if the boss should damage the player on contact
   */
  isLethal(): boolean {
    return !this.isDeadFlag;
  }

  getPosition(): Vector2 {
    return this.position;
  }

  render(renderer: Renderer): void {
    if (this.isDeadFlag) return;

    const color = this.getColor();
    const pulse = 1 + Math.sin(this.elapsedMs * 0.005) * 0.06;
    const invulnFlashing = !this.isVulnerable();
    const renderColor = invulnFlashing && Math.floor(Date.now() / 80) % 2 === 0 ? '#FFFFFF' : color;

    // Outer aura (large soft glow)
    renderer.drawCircle(this.position.x, this.position.y, this.radius * 2.0, color + '22');

    // Main body
    renderer.drawCircle(this.position.x, this.position.y, this.radius * pulse, renderColor);

    // Spiked outline based on type
    const spikes = 12;
    const ctx = renderer.getContext();
    ctx.save();
    ctx.translate(this.position.x, this.position.y);
    ctx.rotate(this.elapsedMs * 0.001);
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < spikes; i++) {
      const a1 = (i / spikes) * Math.PI * 2;
      const a2 = ((i + 0.5) / spikes) * Math.PI * 2;
      const r1 = this.radius * 1.15;
      const r2 = this.radius * 1.35;
      ctx.moveTo(Math.cos(a1) * r1, Math.sin(a1) * r1);
      ctx.lineTo(Math.cos(a2) * r2, Math.sin(a2) * r2);
    }
    ctx.stroke();
    ctx.restore();

    // HP bar above boss
    const barWidth = this.radius * 2.4;
    const barHeight = 6;
    const barY = this.position.y - this.radius - 18;
    const barX = this.position.x - barWidth / 2;
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(barX - 2, barY - 2, barWidth + 4, barHeight + 4);
    ctx.fillStyle = '#333';
    ctx.fillRect(barX, barY, barWidth, barHeight);
    const hpFrac = Math.max(0, this.hp / this.maxHp);
    ctx.fillStyle = hpFrac > 0.5 ? '#00FF66' : hpFrac > 0.25 ? '#FFAA00' : '#FF3030';
    ctx.fillRect(barX, barY, barWidth * hpFrac, barHeight);
  }
}
